import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';

// types
import { ComputeServiceType } from '../../utils/types';

// functions

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

// define the available tools.
export const WEB_TOOLS: ComputeServiceType[] = [
  {
    id: -1,
    site: {
      node: '',
      site: '',
      site_uuid: '',
    },
    resource_type: 'www',
    session_type: 'aladinlite',
    compute_service_id: '',
    service_name: 'Aladin Lite',
    service_url: '',
    is_embedded: true,
    compute_status: 'available',
  },
  {
    id: -1,
    site: {
      node: '',
      site: '',
      site_uuid: '',
    },
    resource_type: 'www',
    session_type: 'skyserver',
    compute_service_id: '',
    service_name: 'SkyServer SDSS',
    service_url: '',
    is_embedded: true,
    compute_status: 'available',
  },
];

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface InteractiveSessionTab {
  compute_service_id?: string;
  url?: string; // we hold a copy of the URL here, so that we don't have disruption when the sessions are being refreshed.
  webSessionType?: string;
  toolComponent?: any;
  uniqueID: number;
  infoDisplayed?: boolean;
  sessionRunning?: boolean;
  stagingComplete?: boolean;
}

export interface InteractiveSessionsState {
  //
  filterMaximised: boolean;
  tab: number;
  tabs: InteractiveSessionTab[];
  expanded: boolean;
  tabbarPinned: boolean;
  displayFullScreenMessage: boolean;

  // sessions.
  sessions: ComputeServiceType[];
  sessionsLoaded: boolean;

  // session-loading flags.
  sessionsLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[];

  // aladin objects.
  aladin: any | null;
  aladinLoading: boolean;
  //
} // InteractiveSessionsState

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: InteractiveSessionsState = {
  filterMaximised: true,
  tab: 0,
  tabs: [{ uniqueID: -1 }],
  expanded: false,
  tabbarPinned: true,
  displayFullScreenMessage: false,
  sessions: WEB_TOOLS,
  sessionsLoaded: false,
  sessionsLoading: [],
  aladin: null,
  aladinLoading: true,
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const interactiveSessionsSlice = createSlice({
  name: 'interactiveSessions',
  initialState: initialState,
  reducers: {
    //
    setFilterMaximised: (
      state: InteractiveSessionsState,
      action: PayloadAction<{ filter: boolean }>,
    ) => {
      state.filterMaximised = action.payload.filter;
    },

    setTab: (state: InteractiveSessionsState, action: PayloadAction<{ tab: number }>) => {
      state.tab = action.payload.tab;
    },

    addTab: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        service: ComputeServiceType;
        toolComponent?: any;
        infoDisplayed?: boolean;
      }>,
    ) => {
      //
      // if a web service is already open, then switch to this tab.
      let tabIndex: number = -1;
      if (action.payload.service.resource_type === 'www') {
        //
        tabIndex = state.tabs.findIndex(
          (item) => item.webSessionType === action.payload.service.session_type,
        );

        if (tabIndex > -1) {
          state.tab = tabIndex;
        }
        //
      }

      // if a session is already open, then switch to this tab.
      if (['canfar', 'srcnet'].includes(action.payload.service.resource_type ?? '')) {
        //
        tabIndex = state.tabs.findIndex(
          (item) => item.compute_service_id === action.payload.service.compute_service_id,
        );

        // set current tab, and also update the info-displayed flag if necessary.
        if (tabIndex > -1) {
          //
          state.tab = tabIndex;

          if (
            (state.tabs[tabIndex].infoDisplayed ?? false) !==
            (action.payload.infoDisplayed ?? false)
          ) {
            state.tabs[tabIndex].infoDisplayed = action.payload.infoDisplayed ?? false;
          }
          //
        }
        //
      }

      // otherwise add a new tab.
      if (tabIndex === -1) {
        //
        const newTab: InteractiveSessionTab = {
          uniqueID: Date.now(),
          infoDisplayed: action.payload.infoDisplayed ?? false,
        };

        if (action.payload.service.resource_type === 'www') {
          newTab.webSessionType = action.payload.service.session_type;
        } else {
          newTab.compute_service_id = action.payload.service.compute_service_id;
          newTab.url = action.payload.service.service_url;
          newTab.sessionRunning =
            action.payload.service.compute_status === 'running' &&
            action.payload.service.overridden_status !== 'removing';
          newTab.stagingComplete = true;
        }

        if (action.payload.toolComponent !== undefined) {
          newTab.toolComponent = action.payload.toolComponent;
        }

        state.tabs.push(newTab);
        state.tab = state.tabs.length - 1;
        //
      }

      // if the service is not suitable to be embedded in the gateway, then open it in another browser tab.
      if (
        action.payload.service.is_embedded !== true &&
        action.payload.infoDisplayed !== true
      ) {
        //
        // open this URL in another browser tab.
        window.open(action.payload.service.service_url, '_blank', 'noreferrer');
        //
      }
      //
    },

    updateTab: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        id: number;
        url?: string;
        infoDisplayed?: boolean;
        sessionRunning?: boolean | number;
        stagingComplete?: boolean | number;
      }>,
    ) => {
      //
      const index: number = state.tabs.findIndex(
        (find) => find.uniqueID === action.payload.id,
      );
      if (index >= 0 && index < state.tabs.length) {
        //
        if (action.payload.url !== undefined) {
          state.tabs[index].url = action.payload.url;
        }
        if (action.payload.infoDisplayed !== undefined) {
          state.tabs[index].infoDisplayed = action.payload.infoDisplayed;
        }
        if (action.payload.sessionRunning === -1) {
          state.tabs[index].sessionRunning = undefined;
        } else if (typeof action.payload.sessionRunning === 'boolean') {
          state.tabs[index].sessionRunning = action.payload.sessionRunning;
        }
        if (action.payload.stagingComplete === -1) {
          state.tabs[index].stagingComplete = undefined;
        } else if (typeof action.payload.stagingComplete === 'boolean') {
          state.tabs[index].stagingComplete = action.payload.stagingComplete;
        }
        //
      }
      //
    },

    closeTab: (
      state: InteractiveSessionsState,
      action: PayloadAction<{ tab: number }>,
    ) => {
      //
      if (action.payload.tab > -1 && action.payload.tab < state.tabs.length) {
        //
        const updatedTabs: InteractiveSessionTab[] = [
          ...state.tabs.slice(0, action.payload.tab),
          ...state.tabs.slice(action.payload.tab + 1),
        ];
        state.tabs = updatedTabs;
        if (state.tab === updatedTabs.length) {
          state.tab = updatedTabs.length - 1;
        }
        //
      }
      //
    },

    setDisplayFullScreenMessage: (
      state: InteractiveSessionsState,
      action: PayloadAction<boolean>,
    ) => {
      //
      state.displayFullScreenMessage = action.payload;
      //
    },

    setExpanded: (state: InteractiveSessionsState, action: PayloadAction<boolean>) => {
      //
      state.expanded = action.payload;
      //
    },

    setSessionRefreshing: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        site_uuid: string[];
        compute_status?: string[];
        resource_type?: string[];
        refreshing: boolean;
      }>,
    ) => {
      //
      let currentState: InteractiveSessionsState = current(state);

      state.sessions = currentState.sessions.map((item) =>
        action.payload.site_uuid.indexOf(item.site.site_uuid ?? '#') > -1 &&
        ((action.payload.compute_status?.indexOf(item.compute_status) ?? -1) > -1 ||
          action.payload.compute_status === undefined) &&
        ((action.payload.resource_type?.indexOf(item.resource_type ?? '#') ?? -1) > -1 ||
          action.payload.resource_type === undefined)
          ? Object.assign({}, item, { refreshing: action.payload.refreshing !== false })
          : Object.assign({}, item),
      );
      //
    },

    setSessions: (
      state: InteractiveSessionsState,
      action: PayloadAction<ComputeServiceType[]>,
    ) => {
      state.sessions = action.payload;
    },

    addSessions: (
      state: InteractiveSessionsState,
      action: PayloadAction<ComputeServiceType[]>,
    ) => {
      state.sessions = [...state.sessions, ...action.payload];
    },

    removeSessions: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        site_uuid?: string[];
        id?: number[];
        resource_type?: string[];
        compute_status?: string[];
        session_id?: string[];
      }>,
    ) => {
      //
      state.sessions = state.sessions.filter(
        (item: ComputeServiceType) =>
          (((action.payload.site_uuid?.indexOf(item.site.site_uuid ?? '#') ?? -1) > -1 ||
            action.payload.site_uuid === undefined) &&
            ((action.payload.id?.indexOf(item.id ?? -1) ?? -1) > -1 ||
              action.payload.id === undefined) &&
            ((action.payload.resource_type?.indexOf(item.resource_type ?? '#') ?? -1) >
              -1 ||
              action.payload.resource_type === undefined) &&
            ((action.payload.compute_status?.indexOf(item.compute_status) ?? -1) > -1 ||
              action.payload.compute_status === undefined) &&
            ((action.payload.session_id?.indexOf(item.compute_service_id) ?? -1) > -1 ||
              action.payload.session_id === undefined)) === false,
      );
      //
    },

    updateSessionComputeServiceId: (
      state: InteractiveSessionsState,
      action: PayloadAction<{ id: number; compute_service_id: string }>,
    ) => {
      const index: number = state.sessions.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index > -1) {
        // the placeholder id being replaced: an empty string for the dialog
        // launch, or a temporary string id for the direct (STANDARD) launch.
        const previousComputeServiceId: string = state.sessions[index].compute_service_id;
        state.sessions[index].compute_service_id = action.payload.compute_service_id;

        // sync tabs that were opened before the CANFAR session id was assigned.
        state.tabs = state.tabs.map((tab) =>
          tab.compute_service_id === previousComputeServiceId
            ? { ...tab, compute_service_id: action.payload.compute_service_id }
            : tab,
        );
      }
    },

    setSessionOverriddenStatus: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        sessionID?: number;
        computeServiceID?: string;
        status?: string;
      }>,
    ) => {
      //
      // retrieve session.
      let index: number = -1;
      if (action.payload.sessionID !== undefined) {
        index = state.sessions.findIndex(
          (item) => item.id === (action.payload.sessionID ?? -1),
        );
      }
      if (action.payload.computeServiceID !== undefined) {
        index = state.sessions.findIndex(
          (item) => item.compute_service_id === (action.payload.computeServiceID ?? -1),
        );
      }
      if (index > -1) {
        //
        let session: ComputeServiceType = state.sessions[index];
        session.overridden_status = action.payload.status;
        state.sessions = [
          ...state.sessions.slice(0, index),
          ...[session],
          ...state.sessions.slice(index + 1),
        ];
        //
      }
      //
    },

    setSessionsLoaded: (
      state: InteractiveSessionsState,
      action: PayloadAction<boolean>,
    ) => {
      state.sessionsLoaded = action.payload;
    },

    setSessionsLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<
        {
          resource_type: string;
          compute_status: string;
          id?: string;
        }[]
      >,
    ) => {
      state.sessionsLoading = action.payload;
    },

    addSessionsLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<
        {
          resource_type: string;
          compute_status: string;
          id?: string;
        }[]
      >,
    ) => {
      state.sessionsLoading = [...state.sessionsLoading, ...action.payload];
    },

    removeSessionsLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<
        {
          resource_type: string;
          compute_status: string;
          id?: string;
        }[]
      >,
    ) => {
      //
      state.sessionsLoading = state.sessionsLoading.filter(
        (item) =>
          action.payload.findIndex(
            (find) =>
              find.resource_type === item.resource_type &&
              find.compute_status === item.compute_status &&
              (find.id === item.id || find.id === undefined),
          ) === -1,
      );
      //
    },

    setAladin: (state: InteractiveSessionsState, action: PayloadAction<any>) => {
      state.aladin = action.payload;
    },

    setAladinLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<boolean>,
    ) => {
      state.aladinLoading = action.payload;
    },

    setTabbarPinned: (
      state: InteractiveSessionsState,
      action: PayloadAction<boolean>,
    ) => {
      //
      state.tabbarPinned = action.payload;
      //
    },

    updateSessionStatus: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        session_id: string;
        status?: string;
        expiry_time?: string;
        connect_url?: string;
      }>,
    ) => {
      //
      state.sessions.forEach((item) => {
        if (
          item.compute_service_id.toLowerCase() ===
          action.payload.session_id.toLowerCase()
        ) {
          //
          if (action.payload.status !== undefined) {
            item.compute_status = action.payload.status.toLowerCase();
          }
          if (action.payload.expiry_time !== undefined) {
            item.expires_at = action.payload.expiry_time;
          }
          if (action.payload.connect_url !== undefined) {
            item.service_url = action.payload.connect_url;
          }
          //
        }
      });
      //
    },
    //
  } /*
  extraReducers: (builder) => {
    builder
    
      // run some code when the runService action finished, if you need to.
      .addCase(runService.fulfilled, (state: InteractiveSessionsState) => {})
  },*/,
});

// expose the interface.
export const {
  setFilterMaximised,
  setTab,
  addTab,
  updateTab,
  closeTab,
  setSessionRefreshing,
  setSessions,
  addSessions,
  removeSessions,
  setDisplayFullScreenMessage,
  setExpanded,
  updateSessionComputeServiceId,
  setSessionOverriddenStatus,
  setSessionsLoaded,
  setSessionsLoading,
  addSessionsLoading,
  removeSessionsLoading,
  setAladin,
  setAladinLoading,
  setTabbarPinned,
  updateSessionStatus,
} = interactiveSessionsSlice.actions;
export default interactiveSessionsSlice.reducer;
